import { useState, useEffect, useCallback, useRef } from 'react';
import AppInfoService from '../services/AppInfoService';
import { useUnsavedChanges } from './useUnsavedChanges';
import { useIdleDetection } from './useIdleDetection';
import { useToast } from './useToast';
import { PingResponse } from 'public-contracts';

interface UseVersionUpdateOptions {
  checkInterval?: number; // Interval in milliseconds to check for updates (default: 5 minutes)
  idleTimeBeforeAutoRefresh?: number; // Idle time in milliseconds before auto-refresh (default: 2 minutes)
  autoRefreshDelay?: number; // Delay in seconds before auto-refresh when idle (default: 10 seconds)
}

export const useVersionUpdate = (options: UseVersionUpdateOptions = {}) => {
  const {
    checkInterval = 1000 * 60 * 5, // 5 minutes
    idleTimeBeforeAutoRefresh = 1000 * 60 * 2, // 2 minutes
    autoRefreshDelay = 10, // 10 seconds
  } = options;

  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<PingResponse | null>(
    null
  );
  const [newVersion, setNewVersion] = useState<PingResponse | null>(null);
  const [showNotification, setShowNotification] = useState(false);
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const appInfoServiceRef = useRef<AppInfoService | null>(null);
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const { hasUnsavedChanges } = useUnsavedChanges();
  const { isIdle } = useIdleDetection({ idleTime: idleTimeBeforeAutoRefresh });
  const { error: showErrorToast } = useToast();

  const handleRefresh = useCallback(
    async (isAuto = false) => {
      if (isAuto && hasUnsavedChanges) {
        // Don't auto-refresh if there are unsaved changes
        console.log('Skipping auto-refresh due to unsaved changes');
        return;
      }

      try {
        // Save new version info
        if (appInfoServiceRef.current) {
          await appInfoServiceRef.current.saveAppInfo();
        }

        // Clear cache
        if ('caches' in window) {
          try {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map((name) => caches.delete(name)));
          } catch (error) {
            console.error('Error clearing cache:', error);
          }
        }

        // Reload the page
        window.location.reload();
      } catch (error) {
        console.error('Error refreshing:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to refresh application';
        showErrorToast(`Refresh failed: ${errorMessage}`, 10000);
      }
    },
    [hasUnsavedChanges]
  );

  // Initialize AppInfoService
  useEffect(() => {
    appInfoServiceRef.current = new AppInfoService();

    // Load current version
    const current = appInfoServiceRef.current.getCurrentVersion();
    setCurrentVersion(current);

    // Set up version checking using pure function
    const handleNewVersion = async () => {
      try {
        const { handleNewVersionDetection } = await import(
          './versionUpdateController'
        );
        const newState = await handleNewVersionDetection(
          appInfoServiceRef.current!
        );

        setNewVersion(newState.newVersion);
        setCurrentVersion(newState.currentVersion);
        setHasNewVersion(newState.hasNewVersion);
        setShowNotification(newState.showNotification);
      } catch (error) {
        console.error('Error handling new version:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to check for updates';
        showErrorToast(`Version check failed: ${errorMessage}`, 10000);
      }
    };

    appInfoServiceRef.current.onNewVersion(handleNewVersion);

    return () => {
      if (appInfoServiceRef.current) {
        appInfoServiceRef.current.offNewVersion();
      }
    };
  }, []);

  // Auto-refresh when idle and no unsaved changes
  useEffect(() => {
    if (hasNewVersion && isIdle && !hasUnsavedChanges && !isAutoRefreshing) {
      // Start countdown for auto-refresh
      setIsAutoRefreshing(true);

      autoRefreshTimerRef.current = setTimeout(() => {
        handleRefresh(true); // true = auto refresh
      }, autoRefreshDelay * 1000);

      return () => {
        if (autoRefreshTimerRef.current) {
          clearTimeout(autoRefreshTimerRef.current);
        }
      };
    } else if (!isIdle || hasUnsavedChanges) {
      // Cancel auto-refresh if user becomes active or has unsaved changes
      if (autoRefreshTimerRef.current) {
        clearTimeout(autoRefreshTimerRef.current);
        autoRefreshTimerRef.current = null;
      }
      setIsAutoRefreshing(false);
    }
  }, [
    hasNewVersion,
    isIdle,
    hasUnsavedChanges,
    isAutoRefreshing,
    autoRefreshDelay,
    handleRefresh,
  ]);

  const handleDelay = useCallback(() => {
    setShowNotification(false);
    // Will check again on next interval
  }, []);

  return {
    hasNewVersion,
    showNotification,
    currentVersion,
    newVersion,
    isAutoRefreshing,
    autoRefreshDelay,
    handleRefresh,
    handleDelay,
  };
};
